import React from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLang, ACTIVE_LANGUAGES as LANGUAGES } from '@/context/LangContext';
import { TID } from '@/constants/testIds';
import {
  LayoutDashboard, BookOpen, PenTool, Users, Bot, Tv, BookMarked,
  UserPlus, Shield, CreditCard, LogOut, Globe, Menu, X, Store, User, Bell, Hand, Trophy, Target, MessageSquare, Inbox,
  ChevronDown, Check, Plus, Brain, Layers, Database
} from 'lucide-react';
import { api } from '@/lib/api';
import { useOnline } from '@/lib/useOnline';
import { drainQueue } from '@/lib/offlineSync';
import OfflineBanner from '@/components/OfflineBanner';
import { useTeams } from '@/context/TeamContext';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

const LOGO = '/logo.png';
const ICON = '/icon.png';

function NavItem({ to, icon: Icon, label, testid, search, badge }) {
  const location = useLocation();
  // For items distinguishing by search params (e.g. /editor?blank=1 vs /editor)
  const customActive = (() => {
    if (search === undefined) return null;
    const samePath = location.pathname === to.split('?')[0];
    const hasSearch = location.search.includes(search);
    return samePath && hasSearch;
  })();
  const exactActive = (() => {
    if (search === undefined) return null;
    // /editor should NOT activate if /editor?blank=1
    return null;
  })();

  return (
    <NavLink
      to={to}
      data-testid={testid}
      end={search !== undefined ? false : false}
      className={({ isActive }) => {
        let active = isActive;
        if (search === undefined) {
          // For default /editor: don't activate if URL has ?blank=1
          if (to === '/editor' && location.search.includes('blank=1')) active = false;
        } else {
          active = !!customActive;
        }
        return `group flex items-center gap-3 px-3 py-2.5 rounded-md font-h uppercase tracking-wide text-sm transition-all border-l-2 ${
          active
            ? 'bg-gold/10 text-gold border-gold'
            : 'text-gray-400 border-transparent hover:text-gold hover:bg-gold/5 hover:border-gold/40'
        }`;
      }}
    >
      <Icon className="w-4 h-4" strokeWidth={1.8} />
      <span className="flex-1">{label}</span>
      {badge > 0 && (
        <span className="ml-auto text-[10px] bg-alert text-white rounded-full px-1.5 min-w-[18px] text-center">{badge}</span>
      )}
    </NavLink>
  );
}

/**
 * NavGroup — collapsible parent item with children rendered as an indented
 * sub-menu. Auto-expands when the current route matches any child.
 *
 * Renders children inside a div so styling is independent of <NavItem> active
 * state. Persists the open/closed flag to localStorage so we don't snap shut
 * on every page change. Single source of truth: `localStorage.pm_nav_<key>`.
 */
function NavGroup({ icon: Icon, label, testid, persistKey, children, defaultOpen }) {
  const location = useLocation();

  // Collect child `to` paths so we can auto-open when any of them is active.
  const childPaths = React.Children.toArray(children)
    .filter(Boolean)
    .map((c) => c?.props?.to)
    .filter(Boolean);
  const hasActiveChild = childPaths.some((p) =>
    location.pathname === p || location.pathname.startsWith(p + '/'),
  );

  const storageKey = `pm_nav_${persistKey}`;
  const [open, setOpen] = React.useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored != null) return stored === '1';
    } catch { /* ignore */ }
    return defaultOpen ?? false;
  });

  // Auto-expand (but never auto-collapse) when navigating to a child route.
  React.useEffect(() => {
    if (hasActiveChild && !open) {
      setOpen(true);
      try { localStorage.setItem(storageKey, '1'); } catch { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveChild]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* ignore */ }
  };

  return (
    <div className="space-y-1" data-testid={testid}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
        className={`w-full group flex items-center gap-3 px-3 py-2.5 rounded-md font-h uppercase tracking-wide text-sm transition-all border-l-2 ${
          hasActiveChild
            ? 'bg-gold/5 text-gold border-gold/60'
            : 'text-gray-400 border-transparent hover:text-gold hover:bg-gold/5 hover:border-gold/40'
        }`}
      >
        <Icon className="w-4 h-4" strokeWidth={1.8} />
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-0' : '-rotate-90'}`}
          strokeWidth={2}
        />
      </button>
      {open && (
        <div className="pl-3 ml-1 border-l border-gold/15 space-y-1">
          {children}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = React.useState(false);
  const [unread, setUnread] = React.useState(0);
  const [unreadNotes, setUnreadNotes] = React.useState(0);
  const [unreadFeedback, setUnreadFeedback] = React.useState(0);

  // Poll the notes inbox every 30s for unread count
  React.useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const r = await api.get('/notes/inbox');
        if (cancelled) return;
        const n = (r.data?.conversations || []).reduce((s, c) => s + (c.unread || 0), 0);
        setUnreadNotes(n);
      } catch { /* ignore */ }
    };
    refresh();
    const id = setInterval(refresh, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  React.useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const r = await api.get('/notifications/unread-count');
        if (!cancelled) setUnread(r.data.unread || 0);
      } catch {}
    };
    fetchUnread();
    const t1 = setInterval(fetchUnread, 20000);
    const onUpd = () => fetchUnread();
    window.addEventListener('pm:notifications-updated', onUpd);
    return () => { cancelled = true; clearInterval(t1); window.removeEventListener('pm:notifications-updated', onUpd); };
  }, [user]);

  React.useEffect(() => {
    if (!user || user.role !== 'admin') return;
    let cancelled = false;
    const fetchFb = async () => {
      try {
        const r = await api.get('/feedback/count');
        if (!cancelled) setUnreadFeedback(r.data?.new || 0);
      } catch { /* ignore */ }
    };
    fetchFb();
    const id = setInterval(fetchFb, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  const role = user?.role;
  const isPlayer = role === 'player';

  const commonNav = (
    <>
      {isPlayer && (
        <>
          {/* Player has a strictly-limited menu: Profile, Playbook, Tasks,
              Roster (view-only), Group, Notes, IQ Simulator. Players don't
              get the full collapsible structure — flat list is faster for them. */}
          <NavItem to="/profile" icon={User} label={t('nav_profile')} testid="nav-profile" />
          <NavItem to="/playbook" icon={BookOpen} label={t('nav_playbook')} testid={TID.navPlaybook} />
          <NavItem to="/iq-simulator" icon={Brain} label={t('nav_iq_simulator') || 'Game IQ Simulator'} testid="nav-iq-simulator" />
          <NavItem to="/iq-score" icon={Trophy} label={t('nav_iq_score') || 'IQ Score'} testid="nav-iq-score" />
          <NavItem to="/shot-analyzer" icon={Target} label={t('nav_shot_analyzer') || 'Shot Analyzer'} testid="nav-shot-analyzer" />
          <NavItem to="/assignments" icon={CreditCard} label={t('nav_assignments')} testid="nav-assignments" />
          <NavItem to="/roster" icon={Users} label={t('nav_roster')} testid={TID.navRoster} />
          <NavItem to="/groups" icon={Users} label={t('nav_groups')} testid="nav-groups" />
          <NavItem to="/notes" icon={Bell} label={t('nav_notes')} testid="nav-notes" badge={unreadNotes} />
        </>
      )}

      {!isPlayer && (
        <>
          {/* ─── Dashboard family ─────────────────────────────────────── */}
          <NavGroup icon={LayoutDashboard} label={t('nav_group_dashboard') || 'Dashboard'} testid="nav-group-dashboard" persistKey="dashboard" defaultOpen>
            <NavItem to="/dashboard" icon={LayoutDashboard} label={t('nav_dashboard')} testid={TID.navDashboard} />
            <NavItem to="/playbook" icon={BookOpen} label={t('nav_playbook')} testid={TID.navPlaybook} />
            {role === 'coach' && (
              <NavItem to="/editor" icon={PenTool} label={t('nav_editor')} testid={TID.navEditor} />
            )}
            {role === 'coach' && (
              <NavItem to="/board" icon={Hand} label={t('nav_board') || 'Drawing Board'} testid="nav-board" />
            )}
          </NavGroup>

          {/* ─── Roster family ────────────────────────────────────────── */}
          <NavGroup icon={Users} label={t('nav_group_roster') || 'Roster'} testid="nav-group-roster" persistKey="roster">
            <NavItem to="/roster" icon={Users} label={t('nav_roster')} testid={TID.navRoster} />
            {role === 'coach' && (
              <NavItem to="/teams" icon={Shield} label={t('nav_teams')} testid="nav-teams" />
            )}
            <NavItem to="/groups" icon={Users} label={t('nav_groups')} testid="nav-groups" />
            <NavItem to="/assignments" icon={CreditCard} label={t('nav_assignments')} testid="nav-assignments" />
            <NavItem to="/notes" icon={Bell} label={t('nav_notes')} testid="nav-notes" badge={unreadNotes} />
            {role === 'coach' && (
              <NavItem to="/study" icon={Bot} label={t('nav_study')} testid="nav-study" />
            )}
          </NavGroup>

          {/* ─── AI Coach family ──────────────────────────────────────── */}
          <NavGroup icon={Bot} label={t('nav_group_ai') || 'AI Coach'} testid="nav-group-ai" persistKey="ai">
            <NavItem to="/ai-coach" icon={Bot} label={t('nav_ai')} testid={TID.navAI} />
            <NavItem to="/iq-simulator" icon={Brain} label={t('nav_iq_simulator') || 'Game IQ Simulator'} testid="nav-iq-simulator" />
            <NavItem to="/iq-score" icon={Trophy} label={t('nav_iq_score') || 'IQ Score'} testid="nav-iq-score" />
            <NavItem to="/shot-analyzer" icon={Target} label={t('nav_shot_analyzer') || 'Shot Analyzer'} testid={role === 'admin' ? 'nav-shot-analyzer-admin' : 'nav-shot-analyzer-coach'} />
            {role === 'coach' && (
              <NavItem to="/live-game" icon={Tv} label={t('nav_live')} testid={TID.navLive} />
            )}
          </NavGroup>

          {/* ─── Marketplace family ───────────────────────────────────── */}
          <NavGroup icon={Store} label={t('nav_group_marketplace') || 'Marketplace'} testid="nav-group-marketplace" persistKey="marketplace">
            {role === 'coach' && (
              <NavItem to="/marketplace" icon={Store} label={t('nav_marketplace')} testid="nav-marketplace" />
            )}
            <NavItem to="/academy" icon={BookMarked} label={t('nav_academy')} testid={TID.navAcademy} />
          </NavGroup>

          {/* ─── My Profile family ────────────────────────────────────── */}
          <NavGroup icon={User} label={t('nav_group_profile') || 'My Profile'} testid="nav-group-profile" persistKey="profile">
            <NavItem to="/profile" icon={User} label={t('nav_profile')} testid="nav-profile" />
            <NavItem to="/invitations" icon={UserPlus} label={t('nav_invitations')} testid={TID.navInvitations} />
            <NavItem to="/notifications" icon={Bell} label={`${t('nav_notifications')}${unread > 0 ? ` (${unread})` : ''}`} testid="nav-notifications" />
            <NavItem to="/blog" icon={BookOpen} label="Blog" testid="nav-blog" />
            <NavItem to="/feedback" icon={MessageSquare} label={t('nav_feedback') || 'Feedback'} testid="nav-feedback" />
            <NavItem to="/achievements" icon={Trophy} label={t('nav_achievements')} testid="nav-achievements" />
          </NavGroup>

          {/* ─── Standalone admin / billing entries ──────────────────── */}
          <NavItem to="/admin/premium" icon={CreditCard} label="Premium Manager" testid={role === 'admin' ? 'nav-admin-premium' : 'nav-premium-coach'} />
          <NavItem to="/pricing" icon={CreditCard} label={t('nav_pricing')} testid={TID.navPricing} />
          <NavItem to="/manual" icon={BookOpen} label={t('nav_manual') || 'Manual'} testid="nav-manual" />

          {/* ─── Admin-only tools (kept flat under the families above) ── */}
          {role === 'admin' && (
            <>
              <NavItem to="/admin" icon={Shield} label={t('nav_admin')} testid={TID.navAdmin} />
              <NavItem to="/admin/feedback" icon={Inbox} label={t('afb_nav') || 'Feedback Inbox'} testid="nav-admin-feedback" badge={unreadFeedback} />
              <NavItem to="/admin/playshub" icon={Layers} label="Plays Hub" testid="nav-admin-playshub" />
              <NavItem to="/admin/yolo-dataset" icon={Database} label="YOLO Dataset" testid="nav-admin-yolo" />
            </>
          )}
        </>
      )}

      {/* Player-only feedback shortcut (no family) */}
      {role === 'player' && (
        <NavItem to="/feedback" icon={MessageSquare} label={t('nav_feedback') || 'Feedback'} testid="nav-feedback-player" />
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-navy text-white flex">
      <OfflineBanner />
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col w-64 border-e border-gold/15 bg-navy-deep p-5 fixed top-0 bottom-0">
        <Link to="/dashboard" className="flex items-center justify-center mb-8 -mx-2">
          <img src={LOGO} alt="PlayMaster IA" className="h-28 w-auto max-w-full object-contain" />
        </Link>

        <nav className="flex flex-col gap-1 flex-1 overflow-y-auto">
          {(role === 'coach' || role === 'admin') && <TeamSwitcher />}
          {commonNav}
        </nav>

        <div className="mt-4 pt-4 border-t border-gold/15 flex flex-col gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid={TID.langSwitcher}
              className="flex items-center gap-2 px-3 py-2 rounded-md border border-gold/30 text-gold hover:bg-gold/10 transition text-sm uppercase tracking-wider font-h"
            >
              <Globe className="w-4 h-4" />
              {LANGUAGES.find(l => l.code === lang)?.flag || 'ES'}
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-card-steel border-gold/30 text-white">
              {LANGUAGES.map(l => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  data-testid={`lang-opt-${l.code}`}
                  className="hover:bg-gold/10 cursor-pointer"
                >
                  <span className="font-mono text-gold me-2 w-8">{l.flag}</span>{l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex items-center gap-3 px-1">
            <div className="w-9 h-9 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center text-gold font-h text-sm">
              {user?.name?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="text-xs flex-1 min-w-0">
              <div className="truncate font-semibold">{user?.name}</div>
              <div className="text-gold text-[10px] uppercase tracking-widest">{role}</div>
            </div>
            <button
              data-testid={TID.logoutBtn}
              onClick={async () => { await logout(); navigate('/login'); }}
              className="p-2 rounded-md border border-gold/30 text-gold hover:bg-alert hover:text-white hover:border-alert transition"
              title={t('logout')}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 bg-navy-deep border-b border-gold/20 px-3 py-2.5 flex items-center justify-between gap-2">
        <Link to="/dashboard" className="flex items-center gap-2 flex-shrink-0">
          <img src={LOGO} alt="PlayMaster IA" className="h-12 w-auto object-contain" />
        </Link>
        {/* Active team chip — compact always-visible reminder of which team
            the coach is currently working on. Opens the same dropdown as
            the sidebar TeamSwitcher so changing team is one tap. */}
        {(role === 'coach' || role === 'admin') && <MobileTeamPill />}
        <div className="flex items-center gap-1 flex-shrink-0">
          <Link to="/notifications" className="relative text-gold p-2" data-testid="mobile-bell">
            <Bell className="w-5 h-5" />
            {unread > 0 && (
              <span className="absolute top-0 right-0 bg-alert text-white text-[10px] rounded-full px-1.5 min-w-[18px] text-center leading-4">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </Link>
          <button onClick={() => setOpen(o => !o)} className="text-gold p-2">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </header>
      {open && (
        <div className="lg:hidden fixed inset-0 top-14 z-50 bg-navy-deep p-5 overflow-y-auto pb-24" onClick={() => setOpen(false)}>
          <nav className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
            {/* Team switcher — mobile drawer header. Same component as the
                desktop sidebar so the coach always has the same "USANDO
                EQUIPO" label + dropdown no matter the viewport. */}
            {(role === 'coach' || role === 'admin') && <TeamSwitcher />}
            {commonNav}
          </nav>
          <div className="mt-6 flex gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
            {LANGUAGES.map(l => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                data-testid={`lang-opt-${l.code}`}
                className={`px-3 py-1.5 rounded border text-xs font-h ${lang === l.code ? 'border-gold bg-gold/15 text-gold' : 'border-steel text-gray-300'}`}
              >
                {l.flag}
              </button>
            ))}
          </div>
          <button
            data-testid={TID.logoutBtn}
            onClick={async (e) => { e.stopPropagation(); await logout(); navigate('/login'); }}
            className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-alert text-white font-h uppercase tracking-wider text-sm"
          >
            <LogOut className="w-4 h-4" /> {t('logout')}
          </button>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 lg:ms-64 pt-16 lg:pt-0 min-h-screen pb-20 lg:pb-0">
        <div className="p-5 lg:p-10 max-w-[1600px] mx-auto">
          {children}
        </div>
      </main>

      {/* Discreet floating feedback button — bottom-right, hidden when
          the user is already on the feedback page so it never duplicates. */}
      {location.pathname !== '/feedback' && (
        <Link
          to="/feedback"
          data-testid="floating-feedback-btn"
          className="hidden lg:flex fixed bottom-5 right-5 z-40 items-center gap-2 px-3.5 py-2 rounded-full bg-gold/15 hover:bg-gold/30 border border-gold/40 text-gold text-[11px] font-mono uppercase tracking-widest backdrop-blur-sm transition-all hover:scale-105"
          title={t('nav_feedback') || 'Feedback'}
        >
          <MessageSquare className="w-3.5 h-3.5" />
          {t('nav_feedback') || 'Feedback'}
        </Link>
      )}

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-navy-deep border-t border-gold/30 flex items-stretch pb-[env(safe-area-inset-bottom)]">
        {(() => {
          const mobileItems = [
            ...(role === 'player' ? [
              { to: '/playbook', icon: BookOpen, label: t('nav_playbook') },
              { to: '/assignments', icon: CreditCard, label: t('nav_assignments') },
              { to: '/roster', icon: Users, label: t('nav_roster') },
              { to: '/groups', icon: Users, label: t('nav_groups') },
              { to: '/notes', icon: Bell, label: t('nav_notes') },
            ] : role === 'coach' ? [
              { to: '/dashboard', icon: LayoutDashboard, label: t('nav_dashboard') },
              { to: '/playbook', icon: BookOpen, label: t('nav_playbook') },
              { to: '/editor', icon: PenTool, label: t('nav_editor') },
              { to: '/ai-coach', icon: Bot, label: t('mob_ia') },
              { to: '/marketplace', icon: Store, label: t('mob_market') },
            ] : [
              { to: '/dashboard', icon: LayoutDashboard, label: t('nav_dashboard') },
              { to: '/admin', icon: Shield, label: t('nav_admin') },
              { to: '/playbook', icon: BookOpen, label: t('nav_playbook') },
            ]),
          ];
          return mobileItems.slice(0, 5).map((it, i) => (
            <NavLink
              key={i}
              to={it.to}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] uppercase tracking-wider font-h ${
                  isActive ? 'text-gold' : 'text-gray-400'
                }`
              }
              data-testid={`mob-nav-${it.to.replace('/', '')}`}
            >
              <it.icon className="w-5 h-5" strokeWidth={1.8} />
              <span>{it.label}</span>
            </NavLink>
          ));
        })()}
      </nav>
    </div>
  );
}

function TeamSwitcher() {
  const navigate = useNavigate();
  const { teams, activeTeam, switchTeam } = useTeams();
  const { t } = useLang();
  const [open, setOpen] = React.useState(false);
  if (!teams || teams.length === 0) {
    return (
      <Link
        to="/teams"
        data-testid="team-switcher-empty"
        className="mb-3 flex items-center justify-center gap-2 px-3 py-2.5 rounded-md border border-gold/40 text-gold hover:bg-gold/10 text-xs font-h uppercase tracking-wider"
      >
        <Plus className="w-4 h-4" /> {t('team_create_first') || t('nav_teams')}
      </Link>
    );
  }
  return (
    <div className="mb-3">
      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold/70 mb-1 px-1">
        {t('team_using') || 'Usando equipo'}:
      </div>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger
          data-testid="team-switcher"
          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-md border-2 border-gold/40 bg-gold/10 hover:bg-gold/15 text-sm"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Shield className="w-4 h-4 text-gold flex-shrink-0" />
            <div className="truncate font-h uppercase tracking-wider text-gold text-sm font-semibold">
              {activeTeam?.name || t('team_none')}
            </div>
          </div>
          <ChevronDown className="w-3.5 h-3.5 text-gold flex-shrink-0" />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 bg-navy-deep border-gold/30">
          {teams.map(team => (
            <DropdownMenuItem
              key={team.id}
              data-testid={`team-switcher-opt-${team.id}`}
              onClick={() => { switchTeam(team.id); setOpen(false); }}
              className="flex items-center justify-between text-sm hover:bg-gold/10 cursor-pointer"
            >
              <span className="truncate">{team.name}</span>
              {team.id === activeTeam?.id && <Check className="w-4 h-4 text-gold flex-shrink-0" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem
            onClick={() => { setOpen(false); navigate('/teams'); }}
            className="border-t border-gold/20 mt-1 pt-2 text-gold text-xs uppercase tracking-wider hover:bg-gold/10 cursor-pointer flex items-center gap-2"
            data-testid="team-switcher-manage"
          >
            <Plus className="w-3.5 h-3.5" /> {t('team_manage')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * MobileTeamPill — compact "USANDO: Mi Equipo" chip in the mobile top bar.
 * Mirrors the sidebar TeamSwitcher dropdown so changing team on tablet/phone
 * never requires hunting through the burger menu.
 */
function MobileTeamPill() {
  const { teams, activeTeam, switchTeam } = useTeams();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  if (!teams || teams.length === 0) return null;
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        data-testid="mobile-team-pill"
        className="min-w-0 max-w-[55%] inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border-2 border-gold/40 bg-gold/10 hover:bg-gold/15 text-[11px] mx-1"
      >
        <Shield className="w-3.5 h-3.5 text-gold flex-shrink-0" />
        <span className="font-h uppercase tracking-wider text-gold truncate">
          {activeTeam?.name || '—'}
        </span>
        <ChevronDown className="w-3 h-3 text-gold flex-shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56 bg-navy-deep border-gold/30">
        <div className="px-2 py-1 text-[10px] font-mono uppercase tracking-[0.25em] text-gold/70">
          Usando equipo
        </div>
        {teams.map(team => (
          <DropdownMenuItem
            key={team.id}
            data-testid={`mobile-team-pill-opt-${team.id}`}
            onClick={() => { switchTeam(team.id); setOpen(false); }}
            className="flex items-center justify-between text-sm hover:bg-gold/10 cursor-pointer"
          >
            <span className="truncate">{team.name}</span>
            {team.id === activeTeam?.id && <Check className="w-4 h-4 text-gold flex-shrink-0" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem
          onClick={() => { setOpen(false); navigate('/teams'); }}
          className="border-t border-gold/20 mt-1 pt-2 text-gold text-xs uppercase tracking-wider hover:bg-gold/10 cursor-pointer flex items-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" /> Administrar equipos
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
